import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Die from '../Die';
import { DiceValue, SpecialDiceType } from '../../types';

/** Delay before destruction fx start, so a freshly placed die lands first */
const SHATTER_DELAY = '0.2s';

// ── AnimatedNumber ───────────────────────────────────────────────────────────
// Counts up/down to the new value and pops on change.

interface AnimatedNumberProps {
  value: number;
  className?: string;
  /** Tint the pop red when the value drops, bright yellow when it rises */
  flash?: boolean;
  /** Tween from this value on mount (e.g. 0 for a game-over tally) */
  countFrom?: number;
  /** Tween length in ms */
  duration?: number;
}

export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({ value, className = '', flash = false, countFrom, duration = 450 }) => {
  const [display, setDisplay] = useState(countFrom ?? value);
  const displayRef = useRef(countFrom ?? value);
  const [pop, setPop] = useState<{ id: number; dir: 'up' | 'down' } | null>(null);
  const rafRef = useRef(0);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      // Without countFrom the first render just shows the value
      if (countFrom === undefined || countFrom === value) return;
    }
    const from = displayRef.current;
    if (from === value) return;

    setPop({ id: Date.now(), dir: value > from ? 'up' : 'down' });
    const start = performance.now();
    cancelAnimationFrame(rafRef.current);
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(from + (value - from) * eased);
      displayRef.current = v;
      setDisplay(v);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    const popTimer = setTimeout(() => setPop(null), 700);
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(popTimer);
    };
  }, [value]);

  const flashColor = flash && pop ? (pop.dir === 'down' ? '#f87171' : '#fde047') : undefined;

  return (
    <span className={`inline-block ${className}`}>
      <span
        key={pop?.id ?? 'initial'}
        className={`inline-block tabular-nums ${pop ? 'score-pop' : ''}`}
        style={flashColor ? { color: flashColor } : undefined}
      >
        {display}
      </span>
    </span>
  );
};

// ── SlotFx ───────────────────────────────────────────────────────────────────
// Wraps a grid slot's die. On placement, FLIP-animates the die flying in from
// the center die card; on compaction, slides the die from its old row.

interface SlotFxProps {
  fxId?: number;
  placed: boolean;
  /** Old row index if this slot's die just slid here after a destruction */
  movedFrom: number | null;
  row: number;
  children: React.ReactNode;
}

export const SlotFx: React.FC<SlotFxProps> = ({ fxId, placed, movedFrom, row, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [anim, setAnim] = useState<{ cls: string; style?: React.CSSProperties } | null>(null);

  useLayoutEffect(() => {
    if (!placed && movedFrom === null) {
      setAnim(null);
      return;
    }
    const el = ref.current;
    if (!el) return;

    if (placed) {
      const origin = document.getElementById('center-die');
      const rect = el.getBoundingClientRect();
      if (!origin || rect.width === 0) {
        setAnim({ cls: 'dice-drop-in' });
        return;
      }
      const from = origin.getBoundingClientRect();
      // Boards can be scaled (mobile / hover) -- convert the viewport delta to local space
      const scale = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;
      const dx = (from.left + from.width / 2 - (rect.left + rect.width / 2)) / scale;
      const dy = (from.top + from.height / 2 - (rect.top + rect.height / 2)) / scale;
      setAnim({
        cls: 'dice-fly-in',
        style: { '--fly-dx': `${dx.toFixed(1)}px`, '--fly-dy': `${dy.toFixed(1)}px` } as React.CSSProperties,
      });
      return;
    }

    const colEl = el.closest('[data-col]');
    const slots = colEl ? colEl.querySelectorAll<HTMLElement>('[data-slot]') : null;
    const pitch = slots && slots.length >= 2 ? slots[1].offsetTop - slots[0].offsetTop : el.offsetHeight + 8;
    const dy = (movedFrom - row) * pitch;
    setAnim({ cls: 'dice-slide-move', style: { '--move-dy': `${dy}px` } as React.CSSProperties });
  }, [fxId, placed, movedFrom, row]);

  return (
    <div ref={ref} className={anim?.cls ?? ''} style={anim?.style}>
      {children}
    </div>
  );
};

// ── DieGhost ─────────────────────────────────────────────────────────────────
// Renders over a slot whose die was just destroyed: the die holds for a beat,
// flashes, then shatters into fragments and sparks.

const FRAGS = [
  { pos: { left: '4%', top: '4%' }, fx: '-26px', fy: '-30px', fr: '-160deg' },
  { pos: { right: '4%', top: '4%' }, fx: '26px', fy: '-34px', fr: '140deg' },
  { pos: { left: '4%', bottom: '4%' }, fx: '-30px', fy: '18px', fr: '120deg' },
  { pos: { right: '4%', bottom: '4%' }, fx: '30px', fy: '22px', fr: '-130deg' },
];

const SPARKS = [
  { fx: '-40px', fy: '-44px', color: '#fde047' },
  { fx: '44px', fy: '-38px', color: '#f87171' },
  { fx: '-46px', fy: '30px', color: '#f87171' },
  { fx: '42px', fy: '36px', color: '#fde047' },
  { fx: '0px', fy: '-52px', color: '#ffffff' },
  { fx: '0px', fy: '46px', color: '#fde047' },
];

export const DieGhost: React.FC<{ value: number | string }> = ({ value }) => (
  <div
    className="absolute inset-0 z-30 pointer-events-none"
    style={{ '--fd': SHATTER_DELAY } as React.CSSProperties}
  >
    {FRAGS.map((f, i) => (
      <div
        key={i}
        className="absolute w-[46%] h-[46%] bg-white border-2 border-black rounded-sm dice-frag"
        style={{ ...f.pos, '--fx': f.fx, '--fy': f.fy, '--fr': f.fr } as React.CSSProperties}
      />
    ))}
    {SPARKS.map((s, i) => (
      <div
        key={`s${i}`}
        className="absolute left-1/2 top-1/2 w-1.5 h-1.5 lg:w-2 lg:h-2 dice-frag"
        style={{ background: s.color, '--fx': s.fx, '--fy': s.fy, '--fr': '90deg' } as React.CSSProperties}
      />
    ))}
    {/* Intact die shown on top until the shatter starts */}
    <div className="absolute inset-0 dice-ghost-hold">
      <Die value={value as DiceValue | SpecialDiceType} size="md" />
    </div>
    <div className="absolute inset-0 rounded-lg bg-white dice-flash" />
  </div>
);

// ── ComboBurst ───────────────────────────────────────────────────────────────
// "x2! / x3!" popup when a placement stacks matching dice in a column.

export const ComboBurst: React.FC<{ combo: number }> = ({ combo }) => (
  <div
    className="combo-burst absolute left-1/2 -top-2 lg:-top-4 z-40 pointer-events-none whitespace-nowrap font-bold text-xs lg:text-2xl text-yellow-300"
    style={{ textShadow: '2px 2px 0 #000, -2px 2px 0 #000, 2px -2px 0 #000, -2px -2px 0 #000' }}
  >
    x{combo}!
  </div>
);

// ── VictoryRain ──────────────────────────────────────────────────────────────
// Full-screen rain of mini dice and pixel confetti for the win ceremony.

const RAIN_COLORS = ['#a855f7', '#fde047', '#f87171', '#22c55e', '#06b6d4'];

export const VictoryRain: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [pieces] = useState(() =>
    Array.from({ length: 26 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      value: (1 + Math.floor(Math.random() * 6)) as DiceValue,
      delay: Math.random() * 0.9,
      dur: 1.8 + Math.random() * 1.6,
      drift: (Math.random() - 0.5) * 160,
      spin: (Math.random() < 0.5 ? -1 : 1) * (360 + Math.random() * 540),
      isDie: i % 3 !== 2,
      color: RAIN_COLORS[i % RAIN_COLORS.length],
      size: 8 + Math.random() * 10,
    }))
  );

  useEffect(() => {
    const timer = setTimeout(onDone, 4600);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <div className="fixed inset-0 z-50 pointer-events-none overflow-hidden">
      {pieces.map((p) => (
        <div
          key={p.id}
          className="dice-rain-piece absolute top-0"
          style={{
            left: `${p.x}%`,
            '--dur': `${p.dur}s`,
            '--delay': `${p.delay}s`,
            '--drift': `${p.drift}px`,
            '--spin': `${p.spin}deg`,
          } as React.CSSProperties}
        >
          {p.isDie ? (
            <Die value={p.value} size="sm" />
          ) : (
            <div style={{ width: p.size, height: p.size, background: p.color }} />
          )}
        </div>
      ))}
    </div>
  );
};

// ── ScoreFloat ───────────────────────────────────────────────────────────────
// Floating "-N" over a column that just lost points to destruction.

export const ScoreFloat: React.FC<{ amount: number }> = ({ amount }) => (
  <div
    className="score-float absolute left-1/2 bottom-4 lg:bottom-8 z-40 pointer-events-none font-bold text-[10px] lg:text-xl text-red-400"
    style={{ '--fd': '0.3s', textShadow: '1px 1px 0 #000' } as React.CSSProperties}
  >
    -{amount}
  </div>
);
